import { useState, useCallback } from 'react';
import { Send, Paperclip, Loader2 } from 'lucide-react';
import Button from '../../../../components/form/button';
import { SupportTicket } from '../../../../types/ticket-type';
import { strReplace } from '../../../../lib/helper-support';

interface MessageInputProps {
    onSend: (content: string, isInternal: boolean) => void;
    ticket?: SupportTicket;
}

const MessageInput = ({ onSend, ticket }: MessageInputProps) => {
    const [content, setContent] = useState('');
    const [isInternal, setIsInternal] = useState(false);
    const [sending, setSending] = useState(false);

    const handleSend = useCallback(async () => {
        if (!content.trim() || sending) return;
        setSending(true);
        onSend(content.trim(), isInternal);
        setContent('');
        setSending(false);
    }, [content, isInternal, onSend, sending]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            handleSend();
        }
    };

    const AutoSuggestions = [
        {
            type: 'acknowledgement',
            message: `

Thank you for reaching out. We’ve received your ticket and our team is currently reviewing it. We’ll update you shortly.`
        },
        {
            type: 'in_progress',
            message: `

We are actively working on your request. We appreciate your patience and will keep you informed as soon as we have an update.`
        },
        {
            type: 'request_more_information',
            message: `

To assist you better, could you please provide a bit more detail about the issue you’re experiencing? Screenshots or error messages would be very helpful.

Looking forward to your response.`
        },
        {
            type: 'issue_resolved',
            message: `

We’re happy to let you know that the issue has been resolved. Please check on your end and confirm that everything is working as expected.`
        },
        {
            type: 'payment_review',
            message: `

We’ve reviewed your payment concern and are currently verifying the transaction. We’ll update you once the review is complete.`
        },
        {
            type: 'awaiting_customer_response',
            message: `

We’re currently waiting for additional information from you to proceed. Kindly reply to this message so we can continue resolving your request.`
        },
        {
            type: 'apology_for_delay',
            message: `

We sincerely apologize for the delay in responding to your ticket. Thank you for your patience. We’re now actively reviewing your request.`
        },
        {
            type: 'escalation_notice',
            message: `

Your request has been escalated to our technical team for further investigation. We’ll update you as soon as we receive feedback from them.`
        },
        {
            type: 'follow_up_confirmation',
            message: `

Just checking in to confirm if the issue has been fully resolved on your end. If you’re still experiencing any problems, please let us know.`
        },
        {
            type: 'ticket_closure',
            message: `
Thank you for contacting support. We will now be closing this ticket.

If you need any further assistance, please feel free to reach out to us anytime.`
        }
    ];

    return (
        <div>
            <div className="box-shadow rounded-2xl border border-gray-200 p-4">
                <textarea
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={isInternal ? 'Write an internal note...' : 'Type your reply...'}
                    rows={3}
                    className={`w-full resize-none rounded-xl border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 transition-colors ${isInternal
                        ? 'border-yellow-500/30 bg-surface-note focus:border-yellow-500 focus:ring-yellow-500/30'
                        : 'border-gray-100 focus:border-primary focus:ring-primary/30'
                        }`}
                />
                <div className="mt-3 flex items-center justify-end">
                    {/* <div className="flex items-center gap-3">
                    <button className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">
                        <Paperclip className="h-4 w-4" />
                    </button>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <button
                            onClick={() => setIsInternal(!isInternal)}
                            className={`relative h-5 w-9 rounded-full transition-colors ${isInternal ? 'bg-status-progress' : 'bg-border'
                                }`}
                        >
                            <span
                                className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-card shadow-sm transition-transform ${isInternal ? 'translate-x-4' : ''
                                    }`}
                            />
                        </button>
                        <span className="text-xs font-medium text-muted-foreground">Internal Note</span>
                    </label>
                </div> */}
                    <div className="flex items-center gap-2">
                        <span className="text-[11px] text-muted-foreground">{content.length}/400</span>
                        <Button
                            onClick={() => { content.length < 400 && content.trim() && handleSend() }}
                            disabled={content.length > 400 || !content.trim() || sending}
                            text={
                                <p className='flex items-center gap-2'>
                                    <Send className="h-4 w-4" />
                                    <span>Send</span>
                                </p>
                            }
                        />
                    </div>
                </div>
            </div>
            <div className='grid gap-3 mt-5'>
                <div className='flex gap-4 themeTextMuted'>
                    <p className=''>Auto-suggestion</p>
                    <p className='text-yellow-600 cursor-pointer' onClick={() => setContent('')}>Clear message</p>
                </div>
                <div className='grid gap-2 md:grid-cols-2'>
                    {AutoSuggestions.map((suggestion, i) => (
                        <div key={i} className='cursor-pointer label border p-2 hover:box-shadow hover:bg-primary/5' onClick={() => setContent(suggestion.message)}>
                            <p className="first-letter:capitalize font-bold">{strReplace(suggestion.type, '_', ' ')}</p>
                            {suggestion.message}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default MessageInput;
